import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import { Tooltip } from 'antd';
import { LuListChecks, LuAlertTriangle } from 'react-icons/lu';
import { PiSpinnerBold } from 'react-icons/pi';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { TextM, TextS, TextSS } from 'components/Text';
import { formatCurrency } from 'helpers/normalizers';
import { PostingsService } from 'modules/escrow/services';
import { Posting } from 'types/Posting';
import { AccountType } from 'types/Account';
import {
	Card,
	Dot,
	Header,
	InProgress,
	Metric,
	MetricLabel,
	Metrics,
	Spinner,
	Title,
	Warn,
} from './styles';

const IN_PROGRESS_POLL_INTERVAL = 5000;

export interface ApprovalSummaryProps {
	accountType: AccountType;
	selectedPostings: Posting[];
	approvalLimit: number;
	approvalLimitActive: boolean;
}

const formatCount = (n: number) =>
	`${n} ${n === 1 ? 'transação' : 'transações'}`;

export const ApprovalSummary = ({
	accountType,
	selectedPostings,
	approvalLimit,
	approvalLimitActive,
}: ApprovalSummaryProps) => {
	const theme = useTheme();
	const queryClient = useQueryClient();

	const { data: inProgress } = useQuery({
		queryKey: ['postingsInProgressSummary', accountType],
		queryFn: () => PostingsService.getInProgressSummary(accountType),
		refetchInterval: query =>
			query.state.data?.has_in_progress
				? IN_PROGRESS_POLL_INTERVAL
				: false,
	});

	const hasInProgress = inProgress?.has_in_progress ?? false;
	const inProgressCount = inProgress?.in_progress_count ?? 0;
	const showInProgress = hasInProgress && inProgressCount > 0;

	const wasInProgress = useRef(false);
	useEffect(() => {
		if (wasInProgress.current && !hasInProgress) {
			queryClient.refetchQueries({ queryKey: ['approvalSummary'] });
		}
		wasInProgress.current = hasInProgress;
	}, [hasInProgress, queryClient]);

	const { data: summary } = useQuery({
		queryKey: ['approvalSummary', accountType],
		queryFn: () => PostingsService.getApprovalSummary(accountType),
		staleTime: 60000,
		refetchInterval: hasInProgress ? IN_PROGRESS_POLL_INTERVAL : false,
	});

	const isOverLimit = (posting: Posting) =>
		approvalLimitActive && posting.amount > approvalLimit;

	const eligiblePostings = selectedPostings.filter(
		posting => !isOverLimit(posting),
	);
	const selectedCount = eligiblePostings.length;
	const selectedTotal = eligiblePostings.reduce(
		(sum, posting) => sum + posting.amount,
		0,
	);

	const isOver = selectedPostings.some(isOverLimit);

	return (
		<Card $over={isOver}>
			<Header>
				<Title $over={isOver}>
					{isOver ? (
						<LuAlertTriangle size={16} />
					) : (
						<LuListChecks size={16} />
					)}
					<TextM
						weight={500}
						color={isOver ? theme.danger : theme.primary}
					>
						Resumo de aprovações
					</TextM>
				</Title>
				<TextS color={theme.textGray}>
					Limite por transação:{' '}
					<TextS as="span" weight="bold" color={theme.textSecondary}>
						{approvalLimitActive
							? formatCurrency(approvalLimit)
							: 'Sem limite'}
					</TextS>
				</TextS>
			</Header>

			<Metrics>
				<Metric>
					<MetricLabel>
						<Dot $color="#CBD5E1" />
						<TextSS color={theme.textGray}>Pendentes</TextSS>
					</MetricLabel>
					<TextM weight="bold" color={theme.textSecondary}>
						{formatCount(summary?.pending_count ?? 0)}
					</TextM>
					<TextSS color={theme.textGray}>
						{formatCurrency(summary?.pending_total ?? 0)}
					</TextSS>
				</Metric>

				<Metric>
					<MetricLabel>
						<Dot $color={theme.primary} />
						<TextSS color={theme.textGray}>Selecionadas</TextSS>
					</MetricLabel>
					<TextM weight="bold" color={theme.textSecondary}>
						{formatCount(selectedCount)}
					</TextM>
					<TextSS color={theme.textGray}>
						{selectedCount > 0
							? formatCurrency(selectedTotal)
							: '—'}
					</TextSS>
				</Metric>

				<Tooltip title="Aprovações realizadas hoje. A execução da transferência ocorre conforme a data agendada de cada transação.">
					<Metric>
						<MetricLabel>
							<Dot $color={theme.success} />
							<TextSS color={theme.textGray}>
								Aprovadas hoje
							</TextSS>
						</MetricLabel>
						<TextM weight="bold" color={theme.success}>
							{formatCount(summary?.approved_today_count ?? 0)}
						</TextM>
						<TextSS color={theme.textGray}>
							{formatCurrency(summary?.approved_today_total ?? 0)}
						</TextSS>
						{showInProgress && (
							<InProgress>
								<Spinner>
									<PiSpinnerBold size={12} />
								</Spinner>
								<TextSS color={theme.textGray}>
									{inProgressCount}{' '}
									{inProgressCount === 1
										? 'transação'
										: 'transações'}{' '}
									em execução
								</TextSS>
							</InProgress>
						)}
					</Metric>
				</Tooltip>

				<Metric>
					<MetricLabel>
						<Dot $color={theme.danger} />
						<TextSS color={theme.textGray}>Rejeitadas hoje</TextSS>
					</MetricLabel>
					<TextM weight="bold" color={theme.danger}>
						{formatCount(summary?.rejected_today_count ?? 0)}
					</TextM>
				</Metric>
			</Metrics>

			{isOver && (
				<Warn>
					<LuAlertTriangle size={16} color={theme.danger} />
					<TextS color={theme.danger}>
						Uma ou mais transações selecionadas ultrapassam o limite
						de {formatCurrency(approvalLimit)} por transação e não
						serão consideradas na aprovação.
					</TextS>
				</Warn>
			)}
		</Card>
	);
};
